import * as React from 'react';
import { Loader2, Lock } from 'lucide-react';
import { Button } from '../primitives/Button';
import { CpanelLogo } from '../icons/CpanelLogo';
import { cn } from '../lib/cn';

/**
 * CpanelButton — single-door launcher into cPanel for the customer's hosting
 * account. Locked decision LD-1 (`memory/cpanel_sso_v2_decisions.md`):
 * cPanel is the universal launcher; no per-app deep-links from the portal.
 *
 * Behaviour matrix:
 *   - status='active'           → calls onIssueSession; on success window.location = url
 *   - status='suspended'        → label flips to "Pay invoice to unlock";
 *                                 navigates to /app/billing/invoices/<id>
 *                                 (no EF call, no audit_log entry)
 *   - other                     → disabled
 *   - loading                   → spinner replaces logo, button disabled
 *
 * Dumb component: takes service shape + an `onIssueSession` callback. Keeps
 * @sighthost/ui free of any Supabase / serviceService coupling — same pattern
 * as EftBankDetailsCard.
 */

type ServiceStatus =
  | 'active'
  | 'pending'
  | 'suspended'
  | 'cancelled'
  | 'terminated'
  | 'fraud';

interface CpanelButtonService {
  cpanel_username: string;
  status: ServiceStatus;
  latestUnpaidInvoiceId?: string | null;
}

interface CpanelSsoLikeError {
  code: string;
  message: string;
  latestUnpaidInvoiceId?: string | null;
}

interface CpanelButtonProps {
  service: CpanelButtonService;
  onIssueSession: (cpanel_username: string) => Promise<{ url: string }>;
  /**
   * Called for non-redirect error codes so the host page can render a toast.
   * 'service_suspended' is auto-handled (navigates to the invoice) and is
   * NOT forwarded.
   */
  onError?: (err: CpanelSsoLikeError) => void;
  variant?: 'primary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  className?: string;
}

function CpanelButton({
  service,
  onIssueSession,
  onError,
  variant = 'primary',
  size = 'md',
  label = 'Open cPanel',
  className,
}: CpanelButtonProps) {
  const [loading, setLoading] = React.useState(false);

  const status = service.status;
  const isSuspended = status === 'suspended';
  const isActive = status === 'active';
  const isOtherDisabled = !isActive && !isSuspended;

  async function handleClick() {
    if (isSuspended) {
      const target = service.latestUnpaidInvoiceId
        ? `/app/billing/invoices/${service.latestUnpaidInvoiceId}`
        : '/app/billing';
      window.location.href = target;
      return;
    }
    if (!isActive) return;
    setLoading(true);
    try {
      const { url } = await onIssueSession(service.cpanel_username);
      window.location.href = url;
    } catch (err) {
      const e = err as CpanelSsoLikeError;
      const code = e.code ?? 'internal_error';
      const message = e.message ?? 'Could not open cPanel.';
      // Race: button thought service was active, but WHM says suspended.
      // Auto-redirect to the unpaid invoice and skip the toast.
      if (code === 'service_suspended') {
        const target = e.latestUnpaidInvoiceId
          ? `/app/billing/invoices/${e.latestUnpaidInvoiceId}`
          : '/app/billing';
        window.location.href = target;
        return;
      }
      onError?.({ code, message, latestUnpaidInvoiceId: e.latestUnpaidInvoiceId });
      setLoading(false);
    }
  }

  // Map CpanelButton's local variant + size enum onto Button's enum.
  // Button has no 'default' variant ('primary' is its default), and uses
  // 'default' for the medium size (where CpanelButton uses 'md').
  const buttonVariant: 'primary' | 'outline' =
    variant === 'primary' ? 'primary' : 'outline';
  const buttonSize: 'sm' | 'default' | 'lg' =
    size === 'md' ? 'default' : size;

  let displayLabel = label;
  let leftIcon: React.ReactNode = <CpanelLogo size={size === 'sm' ? 16 : 20} aria-hidden />;

  if (loading) {
    leftIcon = (
      <Loader2
        className={cn(size === 'sm' ? 'size-4' : 'size-5', 'animate-spin')}
        aria-hidden
      />
    );
    displayLabel = 'Opening cPanel…';
  } else if (isSuspended) {
    displayLabel = 'Pay invoice to unlock';
    leftIcon = <Lock className="size-4" aria-hidden />;
  }

  const disabled = loading || isOtherDisabled;
  const tooltip = isOtherDisabled
    ? `cPanel access is unavailable for status "${status}".`
    : undefined;

  return (
    <Button
      type="button"
      variant={buttonVariant}
      size={buttonSize}
      disabled={disabled}
      onClick={handleClick}
      title={tooltip}
      className={cn('gap-2', className)}
    >
      {leftIcon}
      {displayLabel}
    </Button>
  );
}

export { CpanelButton };
export type { CpanelButtonProps, CpanelButtonService };
